import * as React from "react";
import { Search } from "lucide-react";
import { toast } from "react-toastify";

import {
  useAssignSupplierFamilyMutation,
  useCreateSupplierUploadFolderMutation,
  useGetSupplierFamilyCandidatesQuery,
} from "@/api/suppliers";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import type { ContractFamilyMetadataRequest, SupplierFamilySummary } from "@/types/supplier";
import { useGlobalUpload } from "@/views/drive/upload/globalUploadContext";

import { ContractFamilyMetadataForm } from "./contract-family-metadata-form";
import { contractFolderFsEntryId } from "./contract-family-metadata";

interface Props {
  supplierId: string;
  supplierOwnerId: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onAssigned: () => void;
}

export const AddContractFamilyDialog = ({ supplierId, supplierOwnerId, open, onOpenChange, onAssigned }: Props) => {
  const [searchInput, setSearchInput] = React.useState("");
  const [search, setSearch] = React.useState("");
  const [candidateOffset, setCandidateOffset] = React.useState(0);
  const [message, setMessage] = React.useState<string | null>(null);
  const [ownerError, setOwnerError] = React.useState<string | null>(null);
  const [uploadFolderName, setUploadFolderName] = React.useState("");
  const [selectedCandidate, setSelectedCandidate] = React.useState<SupplierFamilySummary | null>(null);
  const [isUploadFlow, setIsUploadFlow] = React.useState(false);
  const { openUploadDialog } = useGlobalUpload();
  const { data, isFetching, isError, refetch } = useGetSupplierFamilyCandidatesQuery(
    { search: search || undefined, offset: candidateOffset, limit: 50 },
    { skip: !open || !search },
  );
  const [assignFamily, { isLoading }] = useAssignSupplierFamilyMutation();
  const [createUploadFolder, { isLoading: isCreatingUploadFolder }] = useCreateSupplierUploadFolderMutation();

  React.useEffect(() => {
    const timeout = window.setTimeout(() => {
      setSearch(searchInput.trim());
      setCandidateOffset(0);
    }, 250);
    return () => window.clearTimeout(timeout);
  }, [searchInput]);

  React.useEffect(() => {
    if (!open) {
      setSearchInput("");
      setSearch("");
      setMessage(null);
      setOwnerError(null);
      setUploadFolderName("");
      setSelectedCandidate(null);
      setIsUploadFlow(false);
      setCandidateOffset(0);
    }
  }, [open]);

  const assign = async (family: SupplierFamilySummary, metadata: ContractFamilyMetadataRequest) => {
    setMessage(null);
    setOwnerError(null);
    try {
      await assignFamily({ supplierId, familyId: contractFolderFsEntryId(family), data: metadata }).unwrap();
      toast.success("Contract folder linked");
      onOpenChange(false);
      onAssigned();
    } catch (error) {
      const code = (error as { data?: { detail?: { code?: string } } })?.data?.detail?.code;
      if (code === "contract_family_owner_required") {
        setOwnerError("Select an active Contract Family owner");
      } else {
        setMessage("The contract folder could not be linked.");
      }
      refetch();
    }
  };

  const createAndUpload = async (metadata: ContractFamilyMetadataRequest) => {
    const name = metadata.name?.trim() || uploadFolderName.trim();
    if (!name) return;
    setMessage(null);
    setOwnerError(null);
    try {
      const { name: _name, ...data } = metadata;
      const folder = await createUploadFolder({ supplierId, name, data }).unwrap();
      if (!folder.fs_entry_id) throw new Error("Missing folder ID");
      onOpenChange(false);
      onAssigned();
      openUploadDialog({
        fseId: folder.fs_entry_id,
        name: folder.name,
        canUpload: true,
        forceDirectoryPreparation: true,
      });
    } catch (error) {
      const code = (error as { data?: { detail?: { code?: string } } })?.data?.detail?.code;
      if (code === "contract_family_owner_required") {
        setOwnerError("Select an active Contract Family owner");
      } else {
        setMessage("The upload folder could not be created.");
      }
    }
  };

  return (
    <Dialog open={open} onOpenChange={next => !isLoading && !isCreatingUploadFolder && onOpenChange(next)}>
      <DialogContent
        closeDisabled={isLoading || isCreatingUploadFolder}
        className="max-h-[90vh] overflow-y-auto sm:max-w-3xl"
      >
        <>
          <DialogHeader>
            <DialogTitle>Add Contract Family</DialogTitle>
            <DialogDescription>
              Select an existing Drive folder. Linking does not move, copy, or rename it.
            </DialogDescription>
          </DialogHeader>
          {selectedCandidate || isUploadFlow ? (
            <div className="flex flex-col gap-5">
              {message ? (
                <p role="alert" className="rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-800">
                  {message}
                </p>
              ) : null}
              <ContractFamilyMetadataForm
                supplierOwnerId={supplierOwnerId}
                ownerError={ownerError}
                family={selectedCandidate ?? undefined}
                initialName={selectedCandidate?.name ?? uploadFolderName}
                submitLabel={selectedCandidate ? "Link Contract Family" : "Create folder and upload"}
                isSubmitting={isLoading || isCreatingUploadFolder}
                onCancel={() => {
                  setSelectedCandidate(null);
                  setIsUploadFlow(false);
                  setMessage(null);
                  setOwnerError(null);
                }}
                onSubmit={metadata =>
                  selectedCandidate ? assign(selectedCandidate, metadata) : createAndUpload(metadata)
                }
              />
            </div>
          ) : (
            <div className="space-y-3">
              <div className="rounded-md border bg-slate-50 p-3">
                <p className="mb-2 text-sm font-medium text-slate-900">Upload to a new folder</p>
                <div className="flex gap-2">
                  <Input
                    aria-label="New Contract Family folder name"
                    value={uploadFolderName}
                    onChange={event => setUploadFolderName(event.target.value)}
                    placeholder="Folder name"
                  />
                  <Button
                    type="button"
                    onClick={() => {
                      setMessage(null);
                      setIsUploadFlow(true);
                    }}
                    disabled={!uploadFolderName.trim() || isCreatingUploadFolder}
                  >
                    Continue
                  </Button>
                </div>
              </div>
              <p className="text-xs font-medium uppercase tracking-wide text-slate-500">Or link an existing folder</p>
              <div className="relative">
                <Search
                  className="pointer-events-none absolute left-3 top-2.5 h-4 w-4 text-slate-400"
                  aria-hidden="true"
                />
                <Input
                  aria-label="Search Contract Families"
                  value={searchInput}
                  onChange={event => setSearchInput(event.target.value)}
                  className="pl-9"
                />
              </div>
              {message ? (
                <p role="alert" className="rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-800">
                  {message}
                </p>
              ) : null}
              {isFetching ? <p className="py-6 text-center text-sm text-slate-500">Loading candidates…</p> : null}
              {isError ? (
                <p className="py-6 text-center text-sm text-red-700">Candidates could not be loaded.</p>
              ) : null}
              {!search ? (
                <p className="py-6 text-center text-sm text-slate-500">Type to search Drive folders.</p>
              ) : null}
              {search && data?.items.length === 0 ? (
                <p className="py-6 text-center text-sm text-slate-500">No eligible Contract Families found.</p>
              ) : null}
              {data?.items.length ? (
                <ul
                  className="max-h-80 divide-y overflow-y-auto rounded-md border"
                  aria-label="Contract Family candidates"
                >
                  {data.items.map(family => (
                    <li key={family.id} className="flex items-center justify-between gap-4 px-3 py-3">
                      <div className="min-w-0">
                        <p className="truncate text-sm font-medium text-slate-900">{family.name}</p>
                        <p className="text-xs text-slate-500">Existing Drive folder</p>
                      </div>
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={() => setSelectedCandidate(family)}
                        disabled={isLoading}
                      >
                        Add
                      </Button>
                    </li>
                  ))}
                </ul>
              ) : null}
              {data && data.total > data.limit ? (
                <nav className="flex items-center justify-between" aria-label="Contract Family candidate pages">
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    disabled={candidateOffset === 0 || isFetching}
                    onClick={() => setCandidateOffset(Math.max(0, candidateOffset - data.limit))}
                  >
                    Previous
                  </Button>
                  <span className="text-xs text-slate-500">
                    {data.offset + 1}–{Math.min(data.offset + data.items.length, data.total)} of {data.total}
                  </span>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    disabled={data.offset + data.items.length >= data.total || isFetching}
                    onClick={() => setCandidateOffset(candidateOffset + data.limit)}
                  >
                    Next
                  </Button>
                </nav>
              ) : null}
            </div>
          )}
          {!selectedCandidate && !isUploadFlow ? (
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
            </DialogFooter>
          ) : null}
        </>
      </DialogContent>
    </Dialog>
  );
};
